import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import {useGameDetailConfig} from "./GameDetailConfig";
import { logActivity } from "../utils/activity";

// Create WebSocket Context
const WebSocketContext = createContext();

// WebSocket Provider to manage WebSocket connections
export const WebSocketProvider = ({ children, gameId }) => {
    const [connectionStatus, setConnectionStatus] = useState(false);
    const [gameControllerConnected, setGameControllerConnected] = useState(false);
    const [buttons, setButtons] = useState([]);
    const [lockStatus, setLockStatus] = useState("closed");
    const [configs, setConfigs] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState("");
    const { gameDetailConfig } = useGameDetailConfig(); // Access game details
    const [elapsedTime, setElapsedTime] = useState();
    const [timeLeft, setTimeLeft] = useState(3600);
    const [timerIsRunning, setTimerIsRunning] = useState(false);
    const [weight, setWeight] = useState(0);
    const [weightsSolved, setWeightsSolved] = useState([]);
    const [telegraphCurrentMessage, setTelegraphCurrentMessage] = useState([]);
    const [telegraphAllSolved, setTelegraphAllSolved] = useState(false);
    const [telegraphSubSolved, setTelegraphSubSolved] = useState([]);
    const [telegraphConnected, setTelegraphConnected] = useState(false);
    const [letterSolved, setLetterSolved] = useState(false);
    const [letterReadId, setLetterReadId] = useState("");
    const [radioCurrentFreq, setRadioCurrentFreq] = useState("-");
    const [radioCurrentVol, setRadioCurrentVol] = useState("-");
    const [radioCurrentCorrect, setRadioCurrentCorrect] = useState(false);
    const [clockTime, setClockTime] = useState("00:00");
    const [clockAllSolved, setClockAllSolved] = useState(false);
    const [solvedBoard, setSolvedBoard] = useState(new Array(20).fill(false));
    const [rfidEnabled, setRfidEnabled] = useState(new Array(20).fill(false));
    const [rfidDisabled, setRfidDisabled] = useState(new Array(20).fill(false));
    const [solvedRiddles, setSolvedRiddles] = useState([]);
    const [games, setGames] = useState([])
    const [immersionEvents, setImmersionEvents] = useState([])
    const [immersionConnected, setImmersionConnected] = useState(false)
    const [gameLanguage, setGameLanguage] = useState(null)
    const [pictureRelayTriggered, setPictureRelayTriggered] = useState(false);
    const [pictureSolved, setPictureSolved] = useState(false);
    const [pictureConnected, setPictureConnected] = useState(false);
    const [cupboardShelves, setCupboardShelves] = useState([]);
    const [cupboardOpen, setCupboardOpen] = useState(false);
    const [codebreakerStatus, setCodebreakerStatus] = useState(null);
    const [keypadStatus, setKeypadStatus] = useState(null);
    const [gameFlow, setGameFlow] = useState([]);
    const [startKeysSolved, setStartKeysSolved] = useState(false);
    const [startKeyPins, setStartKeyPins] = useState([]);
    const [middlemanConnected, setMiddlemanConnected] = useState(false);
    const [suitcaseStatuses, setSuitcaseStatuses] = useState([]);
    const [waitingForRestart, setWaitingForRestart] = useState(false);
    const [waitingForControllers, setWaitingForControllers] = useState([]);
    const [readyToRestart, setReadyToRestart] = useState(false);
    // The shared GM notes board for this room (see components/NotesBoard). games-cloud pushes the
    // full board as a "notes" message after every post/reset/report, so this is always current.
    const [notes, setNotes] = useState([]);

    const socketRef = useRef(null);
    const connectingRef = useRef(false);
    const connectedRef = useRef(false);
    const gameControllerTimeoutRef = useRef(null);

    const GAME_CONTROLLER_TIMEOUT_MS = 30000;

    const resetGameControllerTimeout = (setConnected) => {
        if (gameControllerTimeoutRef.current) {
            clearTimeout(gameControllerTimeoutRef.current);
        }
        gameControllerTimeoutRef.current = setTimeout(() => {
            setConnected(false);
        }, GAME_CONTROLLER_TIMEOUT_MS);
    };

    useEffect(() => {
        let isUnmounting = false;

        const connectWebSocket = () => {
            const token = localStorage.getItem("accessToken");
            if (!token) {
                console.error("No token found in localStorage");
                return;
            }

            const socketUrl = `${process.env.REACT_APP_GAMES_CLOUD_URL}?token=${encodeURIComponent(token)}`;
            const socket = new WebSocket(socketUrl);
            socketRef.current = socket;

            socket.onopen = () => {
                if (isUnmounting) return;
                setConnectionStatus(true);
                console.log("Connected to WebSocket");
                connectedRef.current = true;

                // Send initial game request
                socket.send(`run_game ${gameDetailConfig.game_id}`);
                getTimerStatus();
                setTimeout(() => getTimerStatus(), 1000);
            };

            socket.onclose = (event) => {
                if (isUnmounting) return;
                if (connectingRef.current) return;

                connectedRef.current = false;
                setConnectionStatus(false);
                setGameControllerConnected(false);
                if (gameControllerTimeoutRef.current) {
                    clearTimeout(gameControllerTimeoutRef.current);
                }
                console.warn("WebSocket connection closed:", event.code, event.reason);

                const attemptReconnection = () => {
                    if (!connectedRef.current && !isUnmounting) {
                        console.log("Attempting to reconnect...");
                        connectWebSocket();

                        setTimeout(() => {
                            if (!connectedRef.current && !isUnmounting) {
                                attemptReconnection();
                            } else {
                                console.log("Reconnection successful.");
                                connectingRef.current = false;
                            }
                        }, 5000);
                    } else {
                        connectingRef.current = false;
                    }
                };

                connectingRef.current = true;
                attemptReconnection();
            };

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data);
                    console.log("Received message:", data);

                    if (data.message_type === "status") {
                        setGameControllerConnected(true);
                        resetGameControllerTimeout(setGameControllerConnected);
                        switch (gameId) {
                            case "Timed buttons":
                                setButtons(data.status);
                                if (data.lock) setLockStatus(data.lock);
                                break;
                            case "nighttrain":
                                if(!data.smart_controller_status.weight || data.smart_controller_status.weight.status === "disconnected") {
                                    setWeight(null);
                                    setWeightsSolved(null);
                                } else {
                                    setWeight(data.smart_controller_status.weight.status.current_weight);
                                    setWeightsSolved(data.smart_controller_status.weight.status.weights_solved_status);
                                }
                                if(!data.smart_controller_status.telegraph || data.smart_controller_status.telegraph.status === "disconnected") {
                                    setTelegraphConnected(false)
                                } else {
                                    setTelegraphCurrentMessage(data.smart_controller_status.telegraph.status.current_message[0]);
                                    setTelegraphConnected(true)
                                    setTelegraphAllSolved(data.smart_controller_status.telegraph.status.all_solved)
                                    setTelegraphSubSolved(data.smart_controller_status.telegraph.status.solved)
                                }

                                if(!data.smart_controller_status.letter || data.smart_controller_status.letter.status === "disconnected") {
                                    setLetterSolved(null);
                                    setLetterReadId(null);
                                } else {
                                    setLetterSolved(data.smart_controller_status.letter.status.recognized);
                                    setLetterReadId(data.smart_controller_status.letter.status.read_id);
                                }

                                if(!data.smart_controller_status.radio || data.smart_controller_status.radio.status === "disconnected") {
                                    setRadioCurrentFreq(null);
                                    setRadioCurrentVol(null);
                                    setRadioCurrentCorrect(null);
                                } else {
                                    setRadioCurrentFreq(data.smart_controller_status.radio.status.current_freq);
                                    setRadioCurrentVol(data.smart_controller_status.radio.status.current_volume);
                                    setRadioCurrentCorrect(data.smart_controller_status.radio.status.currently_correct_freq);
                                }

                                if(!data.smart_controller_status.clock || data.smart_controller_status.clock.status === "disconnected") {
                                    setClockAllSolved(null)
                                    setClockTime("");
                                } else {
                                    let minutes = data.smart_controller_status.clock.status.minute;
                                    if (minutes < 10) {
                                        minutes = "0" + minutes;
                                    }
                                    let hours = data.smart_controller_status.clock.status.hour;
                                    if (hours < 10) {
                                        hours = "0" + hours;
                                    }
                                    setClockTime(hours+ ":" + minutes);
                                    setClockAllSolved(data.smart_controller_status.clock.status.all_solved);
                                }

                                if(!data.smart_controller_status.investigation_board || data.smart_controller_status.investigation_board.status === "disconnected") {
                                    setRfidEnabled(null);
                                } else {
                                    const rfidStatus = data.smart_controller_status.investigation_board.status.rfid_status;
                                    const solvedArray = rfidStatus.map((item) => item.solved ?? false);
                                    setSolvedBoard(new Array(20).fill(false).map((_, i) => solvedArray[i] ?? false));

                                    const rfidEnabled = data.smart_controller_status.investigation_board.status.rfid_status;
                                    const enabledArray = rfidEnabled.map((item) => item.reader_enabled ?? false);
                                    setRfidEnabled(new Array(20).fill(false).map((_, i) => enabledArray[i] ?? false));

                                    const rfidDisabled = data.smart_controller_status.investigation_board.status.rfid_status;
                                    const disabledArray = rfidDisabled.map((item) => item.reader_disabled ?? false);
                                    setRfidDisabled(new Array(20).fill(false).map((_, i) => disabledArray[i] ?? false));

                                    setSolvedRiddles(data.smart_controller_status.investigation_board.status.riddle_pin_status);
                                }

                                if(!data.smart_controller_status.immersion || data.smart_controller_status.immersion.status === "disconnected") {
                                    setImmersionConnected(false);
                                } else {
                                    setImmersionEvents(data.smart_controller_status.immersion.status.events);
                                    setImmersionConnected(true);
                                }

                                if(!data.smart_controller_status.picture || data.smart_controller_status.picture.status === "disconnected") {
                                    setPictureConnected(false);
                                    setPictureRelayTriggered(false);
                                    setPictureSolved(false);
                                } else {
                                    setPictureConnected(true);
                                    setPictureRelayTriggered(data.smart_controller_status.picture.status.relay_triggered);
                                    setPictureSolved(data.smart_controller_status.picture.status.solved);
                                }

                                if(data.language){
                                    setGameLanguage(data.language);
                                } else {
                                    setGameLanguage(null);
                                }

                                break;
                            case "multiportal":
                                setGames(data.games)
                                break;
                            case "spymaster":
                            case "spymaster1":
                                if(!data.smart_controller_status.cupboard || data.smart_controller_status.cupboard.status === "disconnected") {
                                    setCupboardShelves([]);
                                    setCupboardOpen(false);
                                } else {
                                    const status = data.smart_controller_status.cupboard.status;
                                    if (Array.isArray(status.shelves)) {
                                        setCupboardShelves(status.shelves);
                                        setCupboardOpen(status.open ?? false);
                                    } else {
                                        setCupboardShelves([status.rfid1 ?? false, status.rfid2 ?? false]);
                                        setCupboardOpen(status.active ?? false);
                                    }
                                }
                                if (data.smart_controller_status.codebreaker) {
                                    setCodebreakerStatus(data.smart_controller_status.codebreaker.status);
                                } else {
                                    setCodebreakerStatus(null);
                                }
                                if (data.smart_controller_status.keypad_with_screen) {
                                    setKeypadStatus(data.smart_controller_status.keypad_with_screen.status);
                                } else {
                                    setKeypadStatus(null);
                                }
                                if (data.flow) {
                                    setGameFlow(data.flow);
                                }
                                if (data.smart_controller_status.start_keys && data.smart_controller_status.start_keys.status !== "disconnected") {
                                    const skStatus = data.smart_controller_status.start_keys.status;
                                    setStartKeysSolved(skStatus?.solved ?? false);
                                    setStartKeyPins(skStatus?.solve_pins ?? []);
                                    setMiddlemanConnected(true);
                                } else {
                                    setStartKeysSolved(false);
                                    setStartKeyPins([]);
                                    setMiddlemanConnected(false);
                                }
                                const suitcases = Object.entries(data.smart_controller_status)
                                    .filter(([key]) => key.startsWith("suitcase"))
                                    .filter(([, val]) => val.status !== "disconnected")
                                    .map(([key, val]) => ({ id: key, status: val.status }));
                                setSuitcaseStatuses(suitcases);
                                setWaitingForRestart(data.waiting_for_restart ?? false);
                                setWaitingForControllers(data.waiting_for_controllers ?? []);
                                setReadyToRestart(data.ready_to_restart ?? false);
                                if (data.language) {
                                    setGameLanguage(data.language);
                                } else {
                                    setGameLanguage(null);
                                }
                                break;
                            case "urbex":
                                break;
                        }
                        setConnectionStatus(true);
                    }

                    if (data.message_type === "error" && data.error === "Game not found") {
                        setConnectionStatus(false);
                        console.log("Game not found, reconnecting...");
                        setTimeout(() => {
                            if (connectedRef.current) {
                                socket.send(`run_game ${gameDetailConfig.game_id}`);
                            }
                        }, 5000);
                    }

                    if (data.message_type === "event") {
                        if (data.event_type === "game_started") {
                            console.log("Game starting...");
                            setTimeout(() => {
                                if (connectedRef.current) {
                                    console.log("Requesting config list...");
                                    socket.send(`{"message_type": "GetConfigList"}`);
                                    socket.send(`{"message_type": "GetStatus"}`);
                                }
                            }, 5000);
                            setConnectionStatus(false);
                        }
                    }

                    if (data.message_type === "config_list") {
                        switch (gameId) {
                            case "Timed buttons":
                                setButtons(data.status);
                                if (data.lock) setLockStatus(data.lock);
                                setConfigs(data.config_list);
                                setSelectedConfig(data.config_list[data.selected_config]?.id || "");
                                break;
                            case "nighttrain":
                                //console.log("inside config_list");
                                //console.log(data.selected_config)
                                //setConfigs(data.config_list);
                                //setSelectedConfig(data.selected_config || "");
                                break;
                        }
                        setConnectionStatus(true);
                    }

                    if (data.event === "controller_not_ready") {
                        setGameControllerConnected(false);
                        setConnectionStatus(false);
                    }

                    if (data.message_type === "notes") {
                        setNotes(Array.isArray(data.notes) ? data.notes : []);
                    }

                    if (JSON.parse(event.data).message_type === "timer_status") {
                       setElapsedTime(JSON.parse(event.data).elapsed_time);
                        setTimeLeft(JSON.parse(event.data).time_left);
                        setTimerIsRunning(JSON.parse(event.data).is_running);
                    }

                } catch (error) {
                    console.error("Error parsing WebSocket message:", error);
                }
            };

            socket.onerror = (error) => {
                console.error("WebSocket error occurred:", {
                    message: error.message,
                    name: error.name,
                    target: error.target,
                    event: error,
                });
            };
        };

        if (gameDetailConfig) {
            connectWebSocket();
        }

        return () => {
            isUnmounting = true;
            console.log("Closing WebSocket connection...");
            socketRef.current?.close();
        };
    }, [gameDetailConfig]);

    const handleConfigChange = (newConfig) => {
        setSelectedConfig(newConfig);

        const message = {
            message_type: "ChangeConfig",
            payload: { config: newConfig },
        };

        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
            console.log("Configuration changed:", newConfig);
        } else {
            console.warn("WebSocket is not open, unable to send message.");
        }
    };

    const sendStartTimer = () => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(`start_timer ${gameDetailConfig.game_id}`);
            logActivity("timer_start", gameDetailConfig.game_id);
        } else {
            console.warn("WebSocket not connected. Unable to send 'get_games' message.");
        }
    };

    const requestGames = () => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(`get_games ${gameDetailConfig.game_id}`);
        } else {
            console.warn("WebSocket not connected. Unable to send 'start_timer' message.");
        }
    };

    const getTimerStatus = () => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(`timer_status ${gameDetailConfig.game_id}`);
        } else {
            console.warn("WebSocket not connected. Unable to send 'timer_status' message.");
        }
    };

    const sendPauseTimer = () => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(`timer_pause ${gameDetailConfig.game_id}`);
            logActivity("timer_pause", gameDetailConfig.game_id);
        } else {
            console.warn("WebSocket not connected. Unable to send 'pause_timer' message.");
        }
    };

    const sendModifyTimer = (minutes) => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(`timer_modify ${gameDetailConfig.game_id} ${minutes}`);
            logActivity("timer_modify", `${gameDetailConfig.game_id}: ${minutes > 0 ? "+" : ""}${minutes} min`);
        } else {
            console.warn("WebSocket not connected. Unable to send 'timer_modify' message.");
        }
    };

    const sendResetTimer = () => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(`timer_reset ${gameDetailConfig.game_id}`);
            logActivity("timer_reset", gameDetailConfig.game_id);
        } else {
            console.warn("WebSocket not connected. Unable to send 'timer_reset' message.");
        }
    };

    const sendFailure = () => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ message_type: "trigger_failure" }));
        } else {
            console.warn("WebSocket not connected. Unable to send 'trigger_failure' message.");
        }
    };

    const sendStopGame = () => {
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(`stop_game ${gameDetailConfig.game_id}`);
        } else {
            console.warn("WebSocket not connected. Unable to send 'stop_game' message.");
        }
    };

    const sendStartGame = (language) => {
        console.log("Sending start_game message with language:", language.slice(0, 2));
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
            socketRef.current.send(`start_game ${gameDetailConfig.game_id} ${language.slice(0, 2) ?? ""}`);
        } else {
            console.warn("WebSocket not connected. Unable to send 'start_game' message.");
        }
    };

    const sendLanguage = (language) => {
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
            console.warn("WebSocket not connected. Unable to send language message.");
            return;
        }
        const message = {
            message_type: "set_language",
            payload: { language: language.toUpperCase() },
        };
        socketRef.current.send(JSON.stringify(message));
    };

    const sendOverwriteMessage = (readerID, propID) => {
        if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
            console.warn("WebSocket not connected. Unable to send overwrite message.");
            return;
        }

        if (propID !== null) {
            socketRef.current.send(`overwrite_prop ${gameDetailConfig.game_id} ${propID}`);
            logActivity("overwrite_prop", `${gameDetailConfig.game_id}: prop ${propID}`);
        } else if (readerID !== null) {
            socketRef.current.send(`overwrite_flow ${gameDetailConfig.game_id} ${readerID}`);
            logActivity("overwrite_flow", `${gameDetailConfig.game_id}: flow ${readerID}`);
        }
    };

    return (
        <WebSocketContext.Provider
            value={{
                connectionStatus,
                gameControllerConnected,
                buttons,
                lockStatus,
                configs,
                selectedConfig,
                elapsedTime,
                timeLeft,
                timerIsRunning,
                handleConfigChange,
                sendStartTimer,
                getTimerStatus,
                sendPauseTimer,
                sendModifyTimer,
                sendResetTimer,
                sendFailure,
                sendStopGame,
                sendStartGame,
                requestGames,
                games,
                weight,
                weightsSolved,
                telegraphCurrentMessage,
                telegraphAllSolved,
                telegraphSubSolved,
                telegraphConnected,
                letterSolved,
                letterReadId,
                radioCurrentFreq,
                radioCurrentVol,
                radioCurrentCorrect,
                clockTime,
                clockAllSolved,
                solvedBoard,
                rfidEnabled,
                rfidDisabled,
                solvedRiddles,
                sendOverwriteMessage,
                sendLanguage,
                immersionEvents,
                immersionConnected,
                gameLanguage,
                pictureRelayTriggered,
                pictureSolved,
                pictureConnected,
                cupboardShelves,
                cupboardOpen,
                codebreakerStatus,
                keypadStatus,
                gameFlow,
                startKeysSolved,
                startKeyPins,
                middlemanConnected,
                suitcaseStatuses,
                waitingForRestart,
                waitingForControllers,
                readyToRestart,
                notes,
                setNotes,
                socketRef,
                gameDetailConfig,
            }}
        >
            {children}
        </WebSocketContext.Provider>
    );
};

// Custom hook to use WebSocket context
export const useWebSocket = () => {
    return useContext(WebSocketContext);
};